/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable consistent-return */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'
import CurrencyRandIcon from '@offerzen/zenicon/CurrencyRandIcon'
import CurrencyEuroIcon from '@offerzen/zenicon/CurrencyEuroIcon'
import CurrencyPoundIcon from '@offerzen/zenicon/CurrencyPoundIcon'
import CurrencyDollarIcon from '@offerzen/zenicon/CurrencyDollarIcon'
import FormErrorMessage from '../FormErrorMessage'
import FormHelpText from '../FormHelpText'
import AccessibleIcon from '../AccessibleIcon'
import styles, { wrapperTheme, iconColor } from './styles'
import FormWarningMessage from '../FormWarningMessage'

// Chose this value as max as it is the last number before exponential notation
const MAX_VALUE = 9999999999999.99

const currencies = {
  rand: CurrencyRandIcon,
  euro: CurrencyEuroIcon,
  pound: CurrencyPoundIcon,
  dollar: CurrencyDollarIcon,
}

class CurrencyInput extends Component {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    addingCents: false,
    cursorStart: 0,
    cursorEnd: 0,
    counter: 0,
  }

  componentDidUpdate(_, prevState) {
    if (!this.input) return

    // WebKit sets focus to the input after the selection has changed (i.e. after `setSelectionRange` is called).
    //   https://bugs.webkit.org/show_bug.cgi?id=224425#c2
    // This causes a bug whereby the input appears to be "autofocused" when the input is first rendered.
    // To prevent this, the `counter` state is used to determine whether the input has been interacted with.
    if (this.state.counter === 0) return

    if (this.state.counter !== prevState.counter) {
      // If there was any type of interaction with the input, update cursor position
      this.input.setSelectionRange(this.state.cursorStart, this.state.cursorEnd)
    } else if (this.input.selectionStart !== this.state.cursorStart || this.input.selectionEnd !== this.state.cursorEnd) {
      // If the actual cursor position is different than the wanted one, update the cursor
      this.input.setSelectionRange(this.state.cursorStart, this.state.cursorEnd)
    }
  }

  format = value => {
    // If invalid number, format as empty
    if (Number.isNaN(value) || value == null) return ''

    // If 0, return the number as a string
    if (value === '0') return value

    const validValue = Number(value) > MAX_VALUE ? `${MAX_VALUE}` : value

    const decimalDelimiter = validValue.indexOf('.')

    // If no decimal delimiter, we're formatting an integer
    if (decimalDelimiter === -1) {
      // Add thousand delimiter every 3 characters
      const integer = validValue.replace(/(?!^)(?=(?:\d{3})+(?:$))/g, ',')
      // Add decimal delimiter if adding cents
      // If cents aren't allowed, state.addingCents will always be false
      return this.state.addingCents ? `${integer}.` : integer
    }

    if (this.props.allowCents) {
      // Calculate the amount of decimal places required
      const decimalPlaces = validValue.length - 1 - decimalDelimiter
      // Transform to floating string with a max of 2 decimal places
      const floatingString = Number(validValue).toFixed(decimalPlaces > 2 ? 2 : decimalPlaces)
      // // Add thousand delimiter every 3 characters
      const integer = floatingString.replace(/(?!^)(?=(?:\d{3})+(?:\.|$))/g, ',')
      return integer
    }
  }

  parse = value => {
    // Return null if number is null or has no length
    if (value == null || value.length === 0) return null

    // If the number is too big, don't allow changing it
    const checkMax = value.replace(/,/g, '')
    if (Number(checkMax) > MAX_VALUE) return

    const decimalDelimiter = value.indexOf('.')

    // If no decimal delimiter, we're formatting an integer
    if (decimalDelimiter === -1) {
      // Remove the commas added by the formatting
      const stripped = value.replace(/,/g, '')
      // Check that is a valid number (no letters)
      if (Number.isNaN(stripped)) return

      return `${stripped}`
    }

    if (this.props.allowCents) {
      // The number is a float and the delimiter is last
      if (decimalDelimiter === value.length - 1) {
        // If the delimiter is at the end, there's no cents yet, ignore
        // Remove the commas added by the formatting
        const stripped = value.substring(0, decimalDelimiter).replace(/,/g, '')
        // Check that is a valid number (no letters)
        if (Number.isNaN(stripped)) return

        return `${stripped}`
      }

      // If the delimiter is not at the end, split by it, check parts separately and then compose again for parsing
      const integer = value.substring(0, decimalDelimiter).replace(/,/g, '')
      const decimals = value.substring(decimalDelimiter + 1, decimalDelimiter + 3)
      if (Number.isNaN(integer) || Number.isNaN(decimals)) return

      return `${integer}.${decimals}`
    }
  }

  render() {
    const { currency, currencySymbol, meta, input, theme, helpText, className, onChange, onKeyUp, onClick, allowCents, ...props } =
      this.props
    const name = input.name || props.name
    const value = input.value || props.value
    const errored = meta.touched && meta.error
    const warning = meta.touched && meta.warning
    const handler = input.onChange || onChange

    // Format the value to show
    const formatted = this.format(value)
    return (
      <div className={cx(styles.container, className)}>
        <div
          className={cx(
            styles.wrapper,
            wrapperTheme(theme),
            errored && styles.wrapperError,
            warning && styles.wrapperWarning,
            props.readOnly && styles.disabled,
          )}>
          <div className={styles.icon}>
            {currencySymbol ? (
              <span className={cx(styles.currencySymbol, errored && styles.error, warning && styles.warning)}>{currencySymbol}</span>
            ) : (
              <AccessibleIcon icon={currencies[currency]} size={18} color={iconColor(errored, warning)} />
            )}
          </div>
          <input
            className={styles.input}
            type="text"
            {...input}
            {...props}
            name={name}
            value={formatted}
            // eslint-disable-next-line no-return-assign
            ref={el => (this.input = el)}
            onChange={ev => {
              ev.preventDefault()
              // Update the counter to track interaction
              // eslint-disable-next-line react/no-access-state-in-setstate
              this.setState({ counter: this.state.counter + 1 })

              // Strip any non-alphanumeric characters.
              // If `allowCents` is true, don't strip full stops.
              // Also, don't allow changes if new value is over MAX_VALUE.

              const stripRegex = allowCents ? /[^0-9.]/g : /[^0-9]/g
              const strippedValue = ev.target.value.replace(stripRegex, '')
              const newValue = Number(strippedValue.replace(/,/g, '')) > MAX_VALUE ? formatted : strippedValue

              if (newValue === formatted) return

              const parsed = this.parse(newValue)
              if (parsed !== undefined) {
                // Update the flag of whether we're adding cents or not
                // Only do so if cents are allowed
                if (allowCents) {
                  // If the new number is valid
                  const addingCents = newValue.indexOf('.') !== -1
                  this.setState({ addingCents })
                }

                // Calculate the difference in character count, as the formatting function might introduce extra characters
                // Add 1 for the decimal delimiter if we're adding cents
                const decimalDelimiterLength = newValue.endsWith('.') ? 1 : 0
                const charDiff = this.format(parsed).length - newValue.length + decimalDelimiterLength

                // Update the cursor position taking into account the char difference
                // Don't update the cursor position to -1 (the end of the input string)
                // This happens when the selectionStart was at the start of the string and the charDiff was negative
                const cursorStartIndex = Math.max(0, ev.target.selectionStart + charDiff)
                const cursorEndIndex = Math.max(0, ev.target.selectionEnd + charDiff)

                this.setState({ cursorStart: cursorStartIndex, cursorEnd: cursorEndIndex })

                // Invoke callback, if any
                if (handler) handler(parsed)
              }
            }}
            onKeyUp={ev => {
              const key = ev.which
              if ([37, 38, 39, 40].includes(key)) {
                // If the pressed key is any of the arrows, update the cursor position
                this.setState({ cursorStart: ev.target.selectionStart, cursorEnd: ev.target.selectionEnd })
              }

              if (onKeyUp) onKeyUp(ev)
            }}
            onClick={ev => {
              // Update the cursor position and invoke callback if any
              this.setState({ cursorStart: ev.target.selectionStart, cursorEnd: ev.target.selectionEnd })

              if (onClick) onClick(ev)
            }}
            onFocus={ev => {
              // Update the cursor position and invoke callback if any
              this.setState({ cursorStart: ev.target.selectionStart, cursorEnd: ev.target.selectionEnd })

              // Make sure to extract handler from input first, otherwise use top level
              const focusHandler = input.onFocus || props.onFocus
              if (focusHandler) focusHandler(value)
            }}
            onBlur={ev => {
              // Prevent default redux form blur behaviour and invoke callback if any
              ev.preventDefault()

              // Make sure to extract handler from input first, otherwise use top level
              const blurHandler = input.onBlur || props.onBlur
              if (blurHandler) blurHandler(value)
            }}
          />
        </div>
        {Boolean(helpText && !errored && !warning) && <FormHelpText text={helpText} />}
        {Boolean(errored) && <FormErrorMessage text={meta.error} />}
        {Boolean(warning) && <FormWarningMessage text={meta.warning} />}
      </div>
    )
  }
}

const { oneOf, oneOfType, string, node, bool, shape, func } = PropTypes
CurrencyInput.propTypes = {
  currency: oneOf(['rand', 'euro', 'pound', 'dollar']),
  currencySymbol: oneOfType([string, node]),
  allowCents: bool,
  meta: shape({
    touched: bool,
    error: string,
    warning: string,
  }),
  input: shape({
    name: string,
    value: string,
    onChange: func,
    onFocus: func,
    onBlur: func,
  }),
  value: string,
  helpText: string,
  tabIndex: string,
  readOnly: bool,
  theme: oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info']),
  className: string,
  onChange: func,
  onFocus: func,
  onBlur: func,
  onClick: func,
  onKeyUp: func,
}

CurrencyInput.defaultProps = {
  currency: 'rand',
  allowCents: false,
  meta: {},
  input: {},
  tabIndex: '0',
  theme: 'primary',
}

export default CurrencyInput
